import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [subscriptionValid, setSubscriptionValid] = useState(false); // Default to false (safer)
  const [subscriptionMessage, setSubscriptionMessage] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          
          // Always check subscription status from server (not just localStorage)
          try {
            const subResponse = await axios.get(`${API}/auth/me/subscription`);
            const { subscription_valid, subscription_message } = subResponse.data;
            setSubscriptionValid(subscription_valid);
            setSubscriptionMessage(subscription_message);
            localStorage.setItem("subscription_valid", subscription_valid.toString());
            if (subscription_message) {
              localStorage.setItem("subscription_message", subscription_message);
            }
          } catch (subError) {
            // If subscription check fails, default to invalid to be safe
            console.error("Subscription check failed:", subError);
            setSubscriptionValid(false);
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("token");
          localStorage.removeItem("subscription_valid");
          localStorage.removeItem("subscription_message");
          setToken(null);
          setUser(null);
          setSubscriptionValid(false); // Keep false - no access without valid auth
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password, agencyCode) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        agency_code: agencyCode,
      });

      const { access_token, user: userData, subscription_valid, subscription_message } = response.data;
      
      localStorage.setItem("token", access_token);
      localStorage.setItem("subscription_valid", subscription_valid.toString());
      if (subscription_message) {
        localStorage.setItem("subscription_message", subscription_message);
      } else {
        localStorage.removeItem("subscription_message");
      }
      
      setToken(access_token);
      setUser(userData);
      setSubscriptionValid(subscription_valid);
      setSubscriptionMessage(subscription_message);
      
      return { 
        success: true, 
        subscriptionValid: subscription_valid,
        subscriptionMessage: subscription_message
      };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      const status = error.response?.status;
      return { success: false, error: message, status };
    }
  };

  const registerAgency = async (agencyData) => {
    try {
      const response = await axios.post(`${API}/auth/register-agency`, agencyData);

      const { access_token, user: userData, subscription_valid, subscription_message } = response.data;
      
      localStorage.setItem("token", access_token);
      localStorage.setItem("subscription_valid", (subscription_valid ?? false).toString());
      if (subscription_message) {
        localStorage.setItem("subscription_message", subscription_message);
      }
      setToken(access_token);
      setUser(userData);
      setSubscriptionValid(subscription_valid ?? false);
      setSubscriptionMessage(subscription_message);
      
      return { 
        success: true,
        subscriptionValid: subscription_valid ?? false,
        subscriptionMessage: subscription_message
      };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("subscription_valid");
    localStorage.removeItem("subscription_message");
    setToken(null);
    setUser(null);
    setSubscriptionValid(true);
    setSubscriptionMessage(null);
  };

  // Function to update subscription status after successful payment
  const updateSubscriptionStatus = (valid, message = null) => {
    setSubscriptionValid(valid);
    setSubscriptionMessage(message);
    localStorage.setItem("subscription_valid", valid.toString());
    if (message) {
      localStorage.setItem("subscription_message", message);
    } else {
      localStorage.removeItem("subscription_message");
    }
  };

  const updateUser = (patch) => setUser((u) => (u ? { ...u, ...patch } : u));

  const value = {
    user,
    token,
    loading,
    login,
    updateUser,
    registerAgency,
    logout,
    isAuthenticated: !!user,
    subscriptionValid,
    subscriptionMessage,
    updateSubscriptionStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
